import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { ApiFilesGoogleService } from '../../../api/files/routes/google.service';

@Component({
  selector: 'app-login-google',
  standalone: true,
  imports: [CommonModule, NgxSpinnerModule],
  templateUrl: './login-google.component.html',
  styleUrls: ['./login-google.component.sass'],
})
export class LoginGoogleComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly apiFilesGoogle = inject(ApiFilesGoogleService);
  private readonly msalService = inject(MsalService);
  subscription: Subscription;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  constructor() {}

  ngOnInit(): void {
    this.subscription = this.route.queryParams.subscribe((params) => {
      localStorage.setItem('google_access_code', params?.code ?? '');
      const url = localStorage.getItem('previous_url') || '/';
      // localStorage.removeItem('previous_url');
      console.log('[LoginGoogle] previous_url =', url);

      this.apiFilesGoogle.getTokens(params?.code).subscribe({
        next: (res) => {
          console.log('[LoginGoogle] getTokens OK');
          localStorage.setItem('google_tokens', JSON.stringify(res));
          this.navigateWhenAuthenticated(url);
        },
        error: (err) => {
          console.warn('[LoginGoogle] getTokens failed', err);
          // Even if Google token exchange fails, still try to send the user
          // back to where they were — the rest of the app doesn't have to
          // be stuck on the callback URL.
          this.navigateWhenAuthenticated(url);
        },
      });
    });
  }

  /**
   * Polls MSAL every 100 ms for up to 5 s to detect when the session has
   * been re-hydrated after the page reload triggered by Google OAuth. Once
   * an account is detected (and promoted to active if it isn't already)
   * navigates to the previously saved route.
   *
   * Why polling and not just `inProgress$`:
   *  - `MsalBroadcastService.inProgress$` can emit `None` before MSAL has
   *    finished rehydrating accounts from localStorage cache.
   *  - On a page that ISN'T `/` (so `LoginComponent` doesn't run), nothing
   *    else explicitly promotes the first cached account to `active`, so
   *    `getActiveAccount()` can stay `null` for a moment even after MSAL
   *    settles.
   *  - The AuthGuard checks `getActiveAccount()` synchronously, so we MUST
   *    have it set before we trigger the navigation — otherwise the guard
   *    bounces the user to `/`, which loads `LoginComponent`, which fires
   *    `setActiveUser → redirectRoute`, sending the user to the role-based
   *    default page instead of the saved route.
   */
  private navigateWhenAuthenticated(url: string, attempt = 0): void {
    const MAX_ATTEMPTS = 50; // 5 s @ 100 ms cadence

    let active = this.msalService.instance.getActiveAccount();
    if (!active) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalService.instance.setActiveAccount(accounts[0]);
        active = accounts[0];
        console.log(
          `[LoginGoogle] promoted ${accounts[0].username} to active account`
        );
      }
    }

    if (active) {
      console.log(
        `[LoginGoogle] active account ready (${active.username}), navigating to ${url} (attempt ${attempt})`
      );
      this.router
        .navigateByUrl(url, { replaceUrl: true })
        .then((ok) => {
          if (!ok) {
            console.warn(
              '[LoginGoogle] navigateByUrl returned false — guard probably rejected'
            );
          }
        })
        .catch((err) =>
          console.error('[LoginGoogle] navigateByUrl threw', err)
        );
      return;
    }

    if (attempt >= MAX_ATTEMPTS) {
      console.warn(
        '[LoginGoogle] timed out waiting for MSAL session, attempting navigation anyway'
      );
      this.router.navigateByUrl(url, { replaceUrl: true });
      return;
    }

    this.pollTimer = setTimeout(
      () => this.navigateWhenAuthenticated(url, attempt + 1),
      100
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
    }
  }
}
